import { useContext, useMemo, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { getTimeZones } from '@vvo/tzdb';
import { useNavigate } from 'react-router-dom';

import { logout } from 'services/api';
import { AuthState, useLicenseExpires, useCurrentUser } from 'services/auth';
import { DebugState } from 'services/debug';
import { DAY_IN_YEAR_FORMAT } from 'consts/datetime';

import { injectZendesk, openSupportChat } from 'utils/zendesk';
import { formatInTimezone, getConfiguredTimezone } from 'utils/datetime';

import Button from 'components/common/Button';
import {
  CreditCardIcon,
  FeatureToggleIcon,
  TerminalIcon,
  LifeBuoyIcon,
  LogoutIcon,
  UserSettingsIcon,
} from 'components/common/Icons';
import Dropdown from 'components/common/Dropdown';
import { Divider, Separator } from 'components/styled/Layout';
import OnPremiseSupportModal from './OnPremiseSupportModal';

const MenuItemButton = styled(Button)`
  padding: 8px 16px;
  border-radius: 2px;
  width: 100%;
  justify-content: flex-start;

  svg {
    margin-right: 16px;
  }
`;

const VersionNumber = styled.div`
  font-size: 12px;
  padding: 8px 16px;
  font-style: italic;
  display: flex;
  justify-content: flex-end;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const VersionLink = styled.a`
  color: inherit;
  text-decoration: underline;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const UserMenu = () => {
  const navigate = useNavigate();
  const {
    state: { user, token },
  } = useContext(AuthState);
  const { debug } = useContext(DebugState);
  const { hasPermission } = useCurrentUser();
  const timezone = getConfiguredTimezone();
  const theme = useTheme();
  const licenseExpires = useLicenseExpires();
  const isOnPrem = import.meta.env.VITE_DEPLOYMENT_TYPE === 'enterprise';
  const isAdmin = hasPermission('Admin');

  const [isOnPremModalOpen, setIsOnPremModalOpen] = useState(false);

  const handleSupportOpen = () => {
    if (import.meta.env.VITE_DEPLOYMENT_TYPE === 'enterprise') {
      setIsOnPremModalOpen(true);
    } else {
      injectZendesk(() => {
        openSupportChat(user?.username);
      });
    }
  };

  const handleCloseOnPremModal = () => {
    setIsOnPremModalOpen(false);
  };

  const timezoneData = useMemo(() => {
    return getTimeZones({ includeUtc: true }).find((tz) => tz.name === timezone);
  }, [timezone]);

  const userSystemTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const shouldShowTimezone = timezone !== userSystemTimezone;

  return (
    <div className="relative">
      {shouldShowTimezone && (
        <code
          data-test-id="timezone-display"
          className="absolute top-8 font-bold inset-x-0 mx-auto w-max text-[12px] rounded-2 py-0.5 px-1"
          style={{
            color: theme.colors.primary,
          }}
        >
          {timezoneData?.name}
        </code>
      )}
      <Dropdown
        top={58}
        right={10}
        width={265}
        arrow="right"
        align="left"
        trigger={
          <Button variant="text" data-test-id="user-menu-trigger">
            {user && user.username}
          </Button>
        }
      >
        <VersionNumber data-test-id="version-number">
          {isOnPrem ? (
            <VersionLink href="/version" target="_blank" rel="noopener noreferrer">
              Version: {import.meta.env.VITE_RELEASE_VERSION}
            </VersionLink>
          ) : (
            <>Version: {import.meta.env.VITE_RELEASE_VERSION}</>
          )}
        </VersionNumber>
        <MenuItemButton
          variant="text"
          onClick={() => {
            navigate('/user-settings');
          }}
          data-test-category="menu-button"
          data-test-id="menu-button-user-settings"
        >
          <UserSettingsIcon /> User Settings
        </MenuItemButton>
        <MenuItemButton
          variant="text"
          onClick={() => {
            navigate('/usage');
          }}
          data-test-category="menu-button"
          data-test-id="menu-button-usage"
        >
          <CreditCardIcon aria-hidden /> Usage
        </MenuItemButton>
        <MenuItemButton
          variant="text"
          onClick={() => {
            navigate('/api/mql-search');
          }}
          data-test-category="menu-button"
          data-test-id="menu-button-api"
        >
          <TerminalIcon aria-hidden /> API & Documentation
        </MenuItemButton>
        <MenuItemButton
          variant="text"
          onClick={handleSupportOpen}
          data-test-category="menu-button"
          data-test-id="menu-button-chat"
        >
          <LifeBuoyIcon aria-hidden /> Support / Feature Requests
        </MenuItemButton>
        {debug && (
          <>
            <Divider height="8px" />
            <MenuItemButton
              variant="contained"
              color="danger"
              onClick={() => {
                navigate('/debug');
              }}
              data-test-category="menu-button"
              data-test-id="menu-button-debug"
            >
              <FeatureToggleIcon aria-hidden /> Debug
            </MenuItemButton>
          </>
        )}
        <Divider height="8px" />
        <MenuItemButton
          variant="text"
          onClick={() => {
            logout(token);
          }}
          data-test-category="menu-button"
          data-test-id="menu-button-logout"
        >
          <LogoutIcon aria-hidden /> Sign Out
        </MenuItemButton>
        {isOnPrem && licenseExpires && isAdmin && (
          <>
            <Separator className="!mb-0" />
            <VersionNumber data-test-id="license-expiration">
              MixMode License Expiration: {formatInTimezone(licenseExpires, DAY_IN_YEAR_FORMAT)}
            </VersionNumber>
          </>
        )}
      </Dropdown>

      <OnPremiseSupportModal open={isOnPremModalOpen} onClose={handleCloseOnPremModal} />
    </div>
  );
};

export default UserMenu;
